import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import Seo from '../components/Seo';
import { useWallet } from '../context/WalletContext';
import { getUserPortfolio, listUserRedemptions, cancelRedemption } from '../lib/api';
import { useTranslation } from 'react-i18next';

const msInDay = 1000 * 60 * 60 * 24;
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const GROUP_ORDER = ['Today', 'Yesterday', ...DAYS, 'Last Week', 'Older'];

const TX_TYPE_MAP = {
  deposit: { type: 'Deposit', feeType: null },
  withdrawal: { type: 'Withdraw', feeType: null },
  entrance_fee: { type: 'Fee', feeType: 'Entrance Fee' },
  exit_fee: { type: 'Fee', feeType: 'Exit Fee' },
  management_fee: { type: 'Fee', feeType: 'Management Fee' },
  performance_fee: { type: 'Fee', feeType: 'Performance Fee' },
};
const STATUS_MAP = { confirmed: 'completed', pending: 'pending', failed: 'failed' };

const TX_ICONS = {
  Deposit: 'ph-arrow-down-left',
  Withdraw: 'ph-arrow-up-right',
  Fee: 'ph-receipt',
};

function transformTx(tx) {
  const { type, feeType } = TX_TYPE_MAP[tx.tx_type] ?? TX_TYPE_MAP.deposit;
  return {
    id: tx.id,
    type,
    feeType,
    vault: tx.vault_name ?? 'Vault',
    vaultId: tx.vault_id ?? null,
    amount: tx.usdc_amount ?? 0,
    shares: tx.shares_amount ?? 0,
    shareSymbol: tx.vault_symbol ?? 'bG12',
    status: STATUS_MAP[tx.status] ?? 'completed',
    hash: tx.tx_hash ? `${tx.tx_hash.slice(0, 10)}…` : '—',
    fullHash: tx.tx_hash ?? null,
    timestamp: new Date(tx.timestamp ?? tx.created_at),
  };
}

function getGroupName(txDate, now) {
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const txDay = new Date(txDate.getFullYear(), txDate.getMonth(), txDate.getDate());
  const diff = Math.floor((todayStart - txDay) / msInDay);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff >= 2 && diff <= 6) return DAYS[txDay.getDay()];
  if (diff >= 7 && diff <= 13) return 'Last Week';
  return 'Older';
}

function fmtDate(d) {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function fmtShort(d) {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Date Range Picker ────────────────────────────────────────────────────────

function DateRangePicker({ start, end, onChange }) {
  const { t } = useTranslation('transactions');
  const [open, setOpen] = useState(false);
  const [selecting, setSelecting] = useState('start'); // 'start' | 'end'
  const [hovered, setHovered] = useState(null);
  const [viewYear, setViewYear] = useState(() => (start ?? new Date()).getFullYear());
  const [viewMonth, setViewMonth] = useState(() => (start ?? new Date()).getMonth());
  const ref = useRef(null);

  useEffect(() => {
    function onOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  function handleDayClick(date) {
    if (selecting === 'start') {
      onChange({ start: date, end: null });
      setSelecting('end');
    } else {
      if (start && date < start) {
        onChange({ start: date, end: start });
      } else {
        onChange({ start, end: date });
      }
      setSelecting('start');
      setOpen(false);
    }
  }

  function clear() {
    onChange({ start: null, end: null });
    setSelecting('start');
  }

  // Build calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

  const label = start && end
    ? `${fmtShort(start)} – ${fmtShort(end)}`
    : start
      ? `${fmtShort(start)} – …`
      : t('dateRange');

  return (
    <div className="tx-dp-wrap" ref={ref}>
      <button
        className={`tx-dp-trigger${open ? ' open' : ''}${(start || end) ? ' active' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <i className="ph-bold ph-calendar-blank" />
        <span>{label}</span>
        {(start || end) && (
          <span className="tx-dp-clear" onClick={e => { e.stopPropagation(); clear(); }}>
            <i className="ph-bold ph-x" />
          </span>
        )}
        {!(start || end) && <i className="ph-bold ph-caret-down tx-dp-caret" />}
      </button>

      {open && (
        <div className="tx-dp-panel">
          <div className="tx-dp-nav">
            <button className="tx-dp-nav-btn" onClick={prevMonth}><i className="ph-bold ph-caret-left" /></button>
            <span className="tx-dp-month-label">{MONTHS[viewMonth]} {viewYear}</span>
            <button className="tx-dp-nav-btn" onClick={nextMonth}><i className="ph-bold ph-caret-right" /></button>
          </div>

          <div className="tx-dp-weekdays">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <div key={d} className="tx-dp-wd">{d}</div>
            ))}
          </div>

          <div className="tx-dp-grid">
            {cells.map((date, i) => {
              if (!date) return <div key={`e${i}`} />;
              const ts = date.getTime();
              const startTs = start?.getTime();
              const endTs = end?.getTime();
              const hovTs = hovered?.getTime();
              const isStart = startTs === ts;
              const isEnd = endTs === ts;
              const inRange = startTs && endTs && ts > startTs && ts < endTs;
              const hoverRange = startTs && !endTs && hovTs && (
                (hovTs > startTs && ts > startTs && ts < hovTs) ||
                (hovTs < startTs && ts < startTs && ts > hovTs)
              );
              const isHover = hovTs === ts;

              return (
                <button
                  key={ts}
                  className={[
                    'tx-dp-day',
                    isStart ? 'start' : '',
                    isEnd ? 'end' : '',
                    inRange ? 'in-range' : '',
                    hoverRange ? 'hover-range' : '',
                    isHover && !isStart && !isEnd ? 'hovered' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleDayClick(date)}
                  onMouseEnter={() => setHovered(date)}
                  onMouseLeave={() => setHovered(null)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="tx-dp-footer">
            <span className="tx-dp-hint">
              {selecting === 'start' ? t('selectStart') : t('selectEnd')}
            </span>
            {(start || end) && (
              <button className="tx-dp-reset" onClick={clear}>{t('clear')}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Transactions() {
  const { t } = useTranslation('transactions');
  const { walletAddress } = useWallet();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rawTxs, setRawTxs] = useState([]);
  const [pendingRedemptions, setPendingRedemptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [stratFilter, setStratFilter] = useState('All');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [expandedId, setExpandedId] = useState(null);

  const vaultFilter = searchParams.get('vault');

  useEffect(() => {
    if (!walletAddress) { setRawTxs([]); setPendingRedemptions([]); return; }
    async function load() {
      setLoading(true);
      try {
        const [portfolioData, redemptionData] = await Promise.allSettled([
          getUserPortfolio(walletAddress, 'ALL'),
          listUserRedemptions(walletAddress),
        ]);
        if (portfolioData.status === 'fulfilled') {
          setRawTxs((portfolioData.value?.recent_transactions ?? []).map(transformTx));
        }
        if (redemptionData.status === 'fulfilled') {
          setPendingRedemptions(redemptionData.value?.requests ?? []);
        }
      } catch (err) {
        console.error('Transactions load error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [walletAddress]);

  async function handleCancelRedemption(redemptionId) {
    try {
      await cancelRedemption(walletAddress, redemptionId);
      setPendingRedemptions(prev => prev.filter(r => r.id !== redemptionId));
    } catch (err) {
      console.error('Cancel failed:', err);
    }
  }

  function clearVaultFilter() {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('vault');
      return next;
    });
  }

  // Unique vaults for strategy dropdown
  const uniqueVaults = [...new Map(
    rawTxs.filter(tx => tx.vaultId).map(tx => [tx.vaultId, tx.vault])
  ).entries()].map(([id, name]) => ({ id, name }));

  const now = new Date();
  let filtered = [...rawTxs];

  // Vault filter (from URL param)
  if (vaultFilter) filtered = filtered.filter(tx => tx.vaultId === vaultFilter);

  // Strategy filter (from dropdown)
  if (stratFilter !== 'All') filtered = filtered.filter(tx => tx.vaultId === stratFilter);

  // Type filter
  if (typeFilter !== 'All') filtered = filtered.filter(tx => tx.type === typeFilter);

  // Date range filter
  if (dateRange.start) {
    const s = new Date(dateRange.start); s.setHours(0, 0, 0, 0);
    filtered = filtered.filter(tx => tx.timestamp >= s);
  }
  if (dateRange.end) {
    const e = new Date(dateRange.end); e.setHours(23, 59, 59, 999);
    filtered = filtered.filter(tx => tx.timestamp <= e);
  }

  filtered.sort((a, b) => b.timestamp - a.timestamp);

  const filteredVaultName = vaultFilter
    ? (rawTxs.find(tx => tx.vaultId === vaultFilter)?.vault ?? 'Vault')
    : null;

  const grouped = {};
  GROUP_ORDER.forEach(g => { grouped[g] = []; });
  filtered.forEach(tx => {
    const g = getGroupName(tx.timestamp, now);
    if (grouped[g]) grouped[g].push(tx);
  });

  function toggleExpand(id) {
    setExpandedId(prev => prev === id ? null : id);
  }

  return (
    <AppLayout>
      <Seo
        title="Transactions | G12 Labs"
        description="Your G12 Labs transaction history."
        path="/transactions"
        noindex
      />
      <div className="tx-page-container">

        <div className="tx-page-header">
          <div className="tx-page-title-row">
            <h1 className="tx-page-title">{t('title')}</h1>
            {vaultFilter && filteredVaultName && (
              <div className="tx-vault-filter-badge">
                <i className="ph-bold ph-funnel" />
                <span>{filteredVaultName}</span>
                <button className="tx-vault-filter-clear" onClick={clearVaultFilter} title="Clear filter">
                  <i className="ph-bold ph-x" />
                </button>
              </div>
            )}
          </div>

          <div className="tx-controls">
            {/* Type filters */}
            <div className="tx-filters">
              {[
                { key: 'All',     label: t('all') },
                { key: 'Deposit', label: t('deposit') },
                { key: 'Withdraw',label: t('withdraw') },
                { key: 'Fee',     label: t('fee') },
              ].map(f => (
                <button
                  key={f.key}
                  className={`tx-filter-btn${typeFilter === f.key ? ' active' : ''}`}
                  onClick={() => setTypeFilter(f.key)}
                >{f.label}</button>
              ))}
            </div>

            {/* Strategy filter */}
            {uniqueVaults.length > 1 && (
              <div className="tx-select-wrap">
                <i className="ph-bold ph-vault tx-select-icon" />
                <select
                  className="tx-select"
                  value={stratFilter}
                  onChange={e => setStratFilter(e.target.value)}
                >
                  <option value="All">{t('allStrategies')}</option>
                  {uniqueVaults.map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <i className="ph-bold ph-caret-down tx-select-caret" />
              </div>
            )}

            {/* Date range picker */}
            <DateRangePicker
              start={dateRange.start}
              end={dateRange.end}
              onChange={setDateRange}
            />
          </div>
        </div>

        {/* Pending redemptions */}
        {pendingRedemptions.filter(r => r.status === 'pending').map(r => {
          const secsLeft = Math.max(0, Math.floor((new Date(r.unlock_at).getTime() - Date.now()) / 1000));
          const unlocked = secsLeft <= 0;
          const h = Math.floor(secsLeft / 3600);
          const m = Math.floor((secsLeft % 3600) / 60);
          const timeStr = h > 0 ? `${h}h ${m}m` : `${m}m`;
          return (
            <div key={r.id} className="tx-redemption-card">
              <div className="tx-redemption-left">
                <i className={`ph-bold ${unlocked ? 'ph-check-circle' : 'ph-clock-countdown'}`}
                  style={{ color: unlocked ? 'var(--success)' : '#EAB308' }} />
                <div>
                  <span style={{ fontWeight: 600 }}>{unlocked ? t('readyToExecute') : t('pendingWithdrawal')}</span>
                  <span className="tx-redemption-sub">{r.vaults?.name ?? 'Vault'} · {Number(r.shares_amount).toFixed(4)} shares</span>
                </div>
              </div>
              <div className="tx-redemption-right">
                {!unlocked && <span className="tx-redemption-time">{timeStr}</span>}
                <button className="tx-filter-btn" onClick={() => handleCancelRedemption(r.id)}>{t('cancel')}</button>
              </div>
            </div>
          );
        })}

        {/* Transaction list */}
        <div className="tx-list">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="tx-row tx-skeleton">
                <div className="skel-block" style={{ width: 32, height: 32, borderRadius: 8 }} />
                <div className="skel-block" style={{ width: '30%', height: 14 }} />
                <div className="skel-block" style={{ width: '20%', height: 14 }} />
                <div className="skel-block" style={{ width: '15%', height: 14 }} />
                <div className="skel-block" style={{ width: '10%', height: 14 }} />
                <div className="skel-block" style={{ width: '10%', height: 14 }} />
                <div className="skel-block" style={{ width: '12%', height: 14 }} />
              </div>
            ))
          ) : filtered.length === 0 ? (
            <div className="tx-empty-state">
              <i className="ph-bold ph-clock-counter-clockwise" style={{ fontSize: 40, color: 'var(--text-subtle)' }} />
              <p>{walletAddress ? t('noTransactions') : t('connectToView')}</p>
            </div>
          ) : (
            GROUP_ORDER.map(group => {
              const items = grouped[group];
              if (!items || items.length === 0) return null;
              return (
                <div key={group}>
                  <div className="tx-group-label">{group}</div>
                  {items.map(tx => {
                    const isExpanded = expandedId === tx.id;
                    return (
                      <div key={tx.id} className={`tx-row-wrap${isExpanded ? ' expanded' : ''}`}>
                        <div className="tx-row" onClick={() => toggleExpand(tx.id)}>
                          {/* Icon */}
                          <div className="tx-row-icon">
                            <i className={`ph-bold ${TX_ICONS[tx.type] ?? 'ph-receipt'}`} />
                          </div>

                          {/* Type + vault */}
                          <div className="tx-row-info">
                            <span className="tx-row-type">{tx.feeType ?? tx.type}</span>
                            <span className="tx-row-vault">{tx.vault}</span>
                          </div>

                          {/* Date */}
                          <div className="tx-row-date">{fmtDate(tx.timestamp)}</div>

                          {/* Amount */}
                          <div className={`tx-row-amount ${tx.type === 'Deposit' ? 'positive' : tx.type === 'Withdraw' ? 'negative' : ''}`}>
                            {tx.type === 'Deposit' ? '+' : tx.type === 'Withdraw' ? '-' : ''}
                            ${tx.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>

                          {/* Shares */}
                          <div className="tx-row-shares">
                            {tx.shares > 0
                              ? `${tx.shares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ${tx.shareSymbol}`
                              : '—'}
                          </div>

                          {/* Status */}
                          <div className="tx-row-status">
                            <span className={`tx-status-dot ${tx.status}`} />
                            {tx.status}
                          </div>

                          {/* Hash */}
                          <div className="tx-row-hash">
                            {tx.fullHash ? (
                              <a href={`https://etherscan.io/tx/${tx.fullHash}`} target="_blank" rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}>
                                {tx.hash} <i className="ph-bold ph-arrow-square-out" />
                              </a>
                            ) : tx.hash}
                          </div>

                          {/* Expand chevron (mobile) */}
                          <button className="tx-row-expand" onClick={e => { e.stopPropagation(); toggleExpand(tx.id); }}>
                            <i className={`ph-bold ${isExpanded ? 'ph-caret-up' : 'ph-caret-down'}`} />
                          </button>
                        </div>

                        {/* Mobile expandable details */}
                        {isExpanded && (
                          <div className="tx-row-details">
                            <div className="tx-detail-row">
                              <span className="tx-detail-label">Date</span>
                              <span className="tx-detail-value">{fmtDate(tx.timestamp)}</span>
                            </div>
                            <div className="tx-detail-row">
                              <span className="tx-detail-label">Shares</span>
                              <span className="tx-detail-value">
                                {tx.shares > 0
                                  ? `${tx.shares.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ${tx.shareSymbol}`
                                  : '—'}
                              </span>
                            </div>
                            <div className="tx-detail-row">
                              <span className="tx-detail-label">Status</span>
                              <span className="tx-detail-value">
                                <span className={`tx-status-dot ${tx.status}`} style={{ display: 'inline-block', marginRight: 6 }} />
                                {tx.status}
                              </span>
                            </div>
                            <div className="tx-detail-row">
                              <span className="tx-detail-label">Hash</span>
                              <span className="tx-detail-value">
                                {tx.fullHash ? (
                                  <a href={`https://etherscan.io/tx/${tx.fullHash}`} target="_blank" rel="noopener noreferrer">
                                    {tx.hash} <i className="ph-bold ph-arrow-square-out" />
                                  </a>
                                ) : tx.hash}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </AppLayout>
  );
}
